import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { branchService } from '../services/api';
import { Table, Pagination, StatusToggle, PageHeader, useToast, Card } from './common';
import { usePageTitle } from '../context/PageTitleContext';
import { useSidebar } from '../context/SidebarContext';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import usePageState from '../hooks/usePageState';
import { useAuth } from '../context/AuthContext';

const BranchManager = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { getPagePermissions } = useAuth();
  const permissions = getPagePermissions('/admin/branches');
  const { setPageTitle } = usePageTitle();
  const { isCollapsed } = useSidebar();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 30
  });

  const [filters, setFilters] = usePageState('branchManager', {
    currentPage: 1,
    itemsPerPage: 30,
    searchTerm: '',
    startDate: '',
    endDate: '',
  }, ['/admin/branches']);
  const { currentPage, itemsPerPage, searchTerm, startDate, endDate } = filters;
  const setCurrentPage  = (v) => setFilters({ currentPage: v });
  const setItemsPerPage = (v) => setFilters({ itemsPerPage: v });
  const setSearchTerm   = (v) => setFilters({ searchTerm: v });
  const setStartDate    = (v) => setFilters({ startDate: v });
  const setEndDate      = (v) => setFilters({ endDate: v });

  useEffect(() => {
    setPageTitle('Branches');
  }, [setPageTitle]);

  useEffect(() => {
    fetchBranches();
  }, [currentPage, searchTerm, startDate, endDate, itemsPerPage]);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: itemsPerPage };
      if (searchTerm) params.search = searchTerm;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await branchService.getAll(params);
      setBranches(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('Failed to fetch branches');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleView = (branch) => {
    navigate(`/admin/branches/view/${branch.id}`);
  };

  const handleEdit = (branch) => {
    navigate(`/admin/branches/edit/${branch.id}`);
  };

  const handleAddBranch = () => {
    navigate('/admin/branches/create');
  };

  const handleDelete = async (id) => {
    const confirmed = await toast.confirm('Are you sure you want to delete this branch?');
    if (confirmed) {
      try {
        await branchService.delete(id);
        toast.success('Branch deleted successfully');
        fetchBranches();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete branch');
        toast.error(err.response?.data?.error || 'Failed to delete branch');
        console.error(err);
      }
    }
  };

  const handleStatusToggle = async (branch, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [branch.id]: true }));
    
    try {
      await branchService.updateStatus(branch.id, newStatus);
      toast.success(`Branch ${newStatus ? 'activated' : 'deactivated'} successfully`);
      
      // Refresh the branches list
      await fetchBranches();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update branch status');
      console.error(err);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [branch.id]: false }));
    }
  };

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader 
          addButton={permissions.can_add ? {
            label: 'Add Branch',
            onClick: handleAddBranch,
            variant: 'primary'
          } : null}
          filters={{
            search: {
              value: searchTerm,
              onChange: handleSearchChange,
              placeholder: 'Search branches...'
            },
            // dateRange: {
            //   startDate: startDate,
            //   endDate: endDate,
            //   onStartDateChange: handleStartDateChange,
            //   onEndDateChange: handleEndDateChange
            // }
          }}
        />
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded-md border-l-4 border-danger-600">
            {error}
          </div>
        )}

        <Table
          columns={[
            { 
              header: 'S.No', 
              accessor: 'serial',
              width: '80px',
              render: (_, row, index) => (currentPage - 1) * pagination.itemsPerPage + index + 1
            },
            { 
              header: 'Code', 
              accessor: 'code',
              width: '120px',
              render: (value) => (
                <span style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  {value}
                </span>
              )
            },
            { 
              header: 'Name', 
              accessor: 'name',
              width: '250px',
              render: (value) => (
                <span className="text-sm text-gray-900" title={value}>
                  {value && value.length > 35 ? `${value.substring(0, 35)}...` : value}
                </span>
              )
            },
            {
              header: 'Created',
              accessor: 'created_at',
              width: '120px',
              render: (value) => formatDateDDMMYYYY(value),
            },
            {
              header: 'Status',
              accessor: 'status',
              width: '100px',
              align: 'center',
              render: (value, row) => (
                <StatusToggle
                  status={value}
                  onChange={(newStatus) => handleStatusToggle(row, newStatus)}
                  loading={updatingStatus[row.id]}
                  disabled={!permissions.can_edit}
                />
              )
            },
            {
              header: 'Actions',
              accessor: 'actions',
              width: '100px',
              align: 'center',
              actions: [
                ...(permissions.can_edit ? [{ type: 'edit', onClick: handleEdit }] : []),
                ...(permissions.can_delete ? [{ type: 'delete', onClick: (row) => handleDelete(row.id) }] : [])
              ]
            },
          ]}
          data={permissions.can_list ? branches : []}
          onRowClick={permissions.can_view ? handleView : undefined}
          emptyMessage={!permissions.can_list ? 'Permission Denied - You don\'t have permission to view this data' : 'No branches found'}
          loading={loading}
        />
        
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
        
      </Card>
    </div>
  );
};

export default BranchManager;
